"use client";

/** „Produkt“ / „KI“ mega-menu. Mouse hover opens, click/keyboard toggles. Panel anchors to the positioned nav. */
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import {
  type FocusEvent,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import "./product-menu.css";
import { AiMenuFeature } from "./ai-menu-feature";
import { type NavMenuKind, navMenuColumns } from "./product-menu-items";

/** Grace time to cross the gap between trigger and panel. */
const CLOSE_DELAY_MS = 150;

export function ProductMenu(props: {
  readonly kind?: NavMenuKind;
}): ReactNode {
  const kind = props.kind ?? "product";
  const t = useTranslations("marketing.productMenu");
  const [open, setOpen] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<number | null>(null);
  // Hover already opened it — the following click must not close it again.
  const openedByHover = useRef<boolean>(false);
  const panelId = useId();

  const cancelClose = useCallback((): void => {
    if (closeTimer.current === null) return;
    window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const onPointerEnter = useCallback(
    (e: PointerEvent<HTMLDivElement>): void => {
      if (e.pointerType !== "mouse") return;
      cancelClose();
      setOpen((wasOpen) => {
        if (!wasOpen) openedByHover.current = true;
        return true;
      });
    },
    [cancelClose],
  );

  const onPointerLeave = useCallback(
    (e: PointerEvent<HTMLDivElement>): void => {
      if (e.pointerType !== "mouse") return;
      cancelClose();
      closeTimer.current = window.setTimeout(() => {
        openedByHover.current = false;
        setOpen(false);
      }, CLOSE_DELAY_MS);
    },
    [cancelClose],
  );

  const onTriggerClick = useCallback((): void => {
    if (openedByHover.current) {
      openedByHover.current = false;
      return;
    }
    setOpen((wasOpen) => !wasOpen);
  }, []);

  // Tabbing out closes; focus dropping to body (click elsewhere) is the outside-click handler's job.
  const onBlur = useCallback((e: FocusEvent<HTMLDivElement>): void => {
    const next = e.relatedTarget;
    if (!(next instanceof Node) || rootRef.current?.contains(next)) return;
    openedByHover.current = false;
    setOpen(false);
  }, []);

  const close = useCallback((): void => {
    openedByHover.current = false;
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      close();
      triggerRef.current?.focus();
    };
    const onDown = (e: globalThis.PointerEvent): void => {
      if (e.target instanceof Node && rootRef.current?.contains(e.target))
        return;
      close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open, close]);

  useEffect(() => cancelClose, [cancelClose]);

  return (
    <div
      ref={rootRef}
      className="mkpm"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onBlur={onBlur}
    >
      <button
        ref={triggerRef}
        type="button"
        className="mkpm-trigger"
        data-open={open ? "true" : "false"}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onTriggerClick}
      >
        {kind === "ai" ? t("ki") : t("produkt")}
        <ChevronDown
          size={15}
          strokeWidth={2}
          aria-hidden="true"
          className="mkpm-chevron"
        />
      </button>
      <div id={panelId} className="mkpm-panel" hidden={!open}>
        <div className="mkpm-grid">
          {kind === "ai" ? <AiMenuFeature onNavigate={close} /> : null}
          {navMenuColumns(kind, t).map((column) => (
            <div
              key={column.title}
              className={column.span === 2 ? "mkpm-col-wide" : undefined}
            >
              <div className="mkpm-col-title">{column.title}</div>
              <ul
                className="mkpm-list"
                // Fill the left sub-column first, then the right.
                style={
                  column.span === 2
                    ? {
                        gridTemplateRows: `repeat(${Math.ceil(column.items.length / 2)}, auto)`,
                      }
                    : undefined
                }
              >
                {column.items.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      className="mkpm-item"
                      onClick={close}
                    >
                      <span className="mkpm-icon">
                        <item.Icon
                          size={15}
                          strokeWidth={1.9}
                          aria-hidden="true"
                        />
                      </span>
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
